import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';

import { TranslatePipe } from '@ngx-translate/core';

import { MatIcon } from '@angular/material/icon';

/** One stop on the route, as the nav needs to read it. */
export interface StepNavEntry {
  id: string;
  labelKey: string;
  /** Answered already, so the step can be returned to rather than merely reached. */
  completed: boolean;
}

/**
 * A route, not a progress bar: any answered step can be returned to, and an unanswered one is drawn
 * as an outline rather than hidden, so a manager can see what the form will still ask before
 * starting it.
 *
 * `aria-current="step"` rather than `aria-pressed`: these are places in a flow, not switches.
 */
@Component({
  selector: 'guarantees-step-nav',
  standalone: true,
  imports: [MatIcon, TranslatePipe],
  template: `
    <nav class="flex items-stretch gap-1">
      @for (entry of steps(); track entry.id; let index = $index) {
        <button
          class="flex min-w-0 flex-1 items-center gap-1.5 rounded-lg border px-2 py-1.5 text-left transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none motion-reduce:transition-none"
          type="button"
          [class]="
            entry.id === current()
              ? 'border-primary-400 bg-primary-50 text-primary-800'
              : entry.completed
                ? 'border-neutral-200 text-neutral-600 hover:bg-neutral-50'
                : 'border-dashed border-neutral-200 text-neutral-400 hover:bg-neutral-50'
          "
          [attr.aria-current]="entry.id === current() ? 'step' : null"
          (click)="selected.emit(entry.id)">
          <span
            class="inline-flex size-4 shrink-0 items-center justify-center rounded-full text-xs font-bold tabular-nums"
            [class]="entry.completed ? 'bg-success-100 text-success-700' : entry.id === current() ? 'bg-primary-600 text-neutral-0' : 'bg-neutral-100 text-neutral-400'">
            @if (entry.completed) {
              <mat-icon class="h-3! w-3! text-xs! leading-3!">check</mat-icon>
            } @else {
              {{ index + 1 }}
            }
          </span>
          <span class="min-w-0 truncate text-sm font-semibold">{{ entry.labelKey | translate }}</span>
        </button>
      }
    </nav>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StepNavComponent {
  steps = input.required<StepNavEntry[]>();
  current = input.required<string>();

  selected = output<string>();
}
